import axios from "axios";
const baseURL = process.env.REACT_APP_API_BASE_URL;

const api = axios.create({
  baseURL,
  withCredentials: true,
});

let accessToken = null;

export function setAccessToken(token) {
  accessToken = token;
}

export const clearAccessToken = () => {
  accessToken = null;
};

api.interceptors.request.use(
  async (config) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 403 &&
      error.response?.data?.code === "APP_ACCESS_DISABLED"
    ) {
      window.dispatchEvent(new CustomEvent("app:access-disabled"));
    }

    const isAuthRoute = originalRequest?.url?.includes("/auth/");

    if (originalRequest?.skipAuthRefresh || isAuthRoute) {
      if (originalRequest?.skipAuthRefresh && accessToken) {
        await forceLogout();
      }

      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => api(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await axios.post(
          `${baseURL}/auth/refresh`,
          {},
          { withCredentials: true, skipAuthRefresh: true },
        );

        const newAccessToken = res.data.accessToken;
        setAccessToken(newAccessToken);

        processQueue();

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);

        if (accessToken) {
          await forceLogout();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  },
);

async function forceLogout() {
  try {
    await axios.post(`${baseURL}/auth/logout`, {}, { withCredentials: true });
  } catch {}

  clearAccessToken();

  window.history.replaceState({}, "", "/");
}

export default api;
